<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  AmbientFabricBackground,
  UiButton,
  UiCard,
  UiIcon,
  UiLogo,
  UiText,
  useApi,
  type ExpertClaim,
} from '@shopexperts/shared';
import { CLAIM_TOKEN_LENGTH } from '@shopexperts/legacy-api';
import { listClaimContexts, type ClaimContext } from '@/app/composables/claim/claimContexts';
import { extractServerMessage, extractStatus } from '@/app/composables/claim/claimApiErrors';
import { useResendClaimVerification } from '@/app/composables/claim/useResendClaimVerification';
import { getWebsiteOrigin } from '@/app/composables/claim/websiteOrigin';

type ViewState = 'invalid-link' | 'verifying' | 'success' | 'not-found' | 'conflict' | 'expired' | 'error';

const route = useRoute();
const api = useApi();
const websiteOrigin = getWebsiteOrigin();
const contactUrl = `${websiteOrigin}/contact`;

const view = ref<ViewState>('verifying');
const claim = ref<ExpertClaim | null>(null);
const errorMessage = ref('');
const claimContexts = ref<ClaimContext[]>([]);

const resend = useResendClaimVerification();

const token = computed(() => typeof route.query.token === 'string' ? route.query.token : '');

/** Invalid/expired links share the same recovery affordances. */
const showRecovery = computed(() => ['invalid-link', 'not-found', 'expired'].includes(view.value));

const singleContext = computed<ClaimContext | null>(() =>
  claimContexts.value.length === 1 ? claimContexts.value[0] ?? null : null,
);

const stateTitle = computed(() => {
  switch (view.value) {
    case 'verifying':
      return 'Verifying your email...';
    case 'success':
      return 'Your email is verified';
    case 'invalid-link':
    case 'not-found':
      return 'Invalid verification link';
    case 'conflict':
      return 'This claim can no longer be verified';
    case 'expired':
      return 'This verification link has expired';
    default:
      return 'Verification failed';
  }
});

onMounted(async () => {
  claimContexts.value = listClaimContexts();

  // The API requires a token of exactly 64 characters — anything else can only
  // produce a 422, so treat it as an invalid link without calling the API.
  if (token.value.length !== CLAIM_TOKEN_LENGTH) {
    view.value = 'invalid-link';
    return;
  }

  await verify();
});

async function verify() {
  view.value = 'verifying';
  errorMessage.value = '';

  try {
    const result = await api.public.expertClaims.verify({
      body: { token: token.value },
    });
    claim.value = result.claim;
    view.value = 'success';
  } catch (error) {
    const status = extractStatus(error);
    if (status === 404) {
      view.value = 'not-found';
    } else if (status === 409) {
      view.value = 'conflict';
    } else if (status === 410) {
      view.value = 'expired';
    } else {
      errorMessage.value = extractServerMessage(error)
        || 'Something went wrong while verifying your email. Please try again.';
      view.value = 'error';
    }
  }
}

async function handleResend(context: ClaimContext) {
  await resend.send(context.expert_slug, context.email);
}
</script>

<template>
  <div class="relative min-h-screen overflow-hidden flex items-center justify-center px-4">
    <div class="absolute inset-0 w-full h-full z-0">
      <AmbientFabricBackground :settings="{ class: 'w-full h-full' }" />
    </div>

    <UiCard padding="lg" class="relative z-10 w-full max-w-[550px]">
      <div class="flex flex-col items-center justify-center gap-6">
        <UiLogo size="md" class="mb-4" />

        <!-- Verifying -->
        <template v-if="view === 'verifying'">
          <div class="size-12 animate-spin rounded-full border-b-2 border-se-dark" />
          <div class="flex flex-col gap-2 text-center">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              Please wait while we verify your email address.
            </UiText>
          </div>
        </template>

        <!-- Success (idempotent — also shown for previously verified claims) -->
        <template v-else-if="view === 'success' && claim">
          <UiIcon name="check-circle-broken" class="size-16 text-se-green" data-testid="claim-verify-success" />
          <div class="flex flex-col gap-2 text-center max-w-sm">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              Your email <span class="font-semibold">{{ claim.email }}</span> is verified for the claim on
              <span class="font-semibold">{{ claim.expert.name }}</span>.
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              If your claim is still under review, our team will get back to you within 2-3 business days.
            </UiText>
          </div>
        </template>

        <!-- Invalid link (missing/malformed token or 404) -->
        <template v-else-if="view === 'invalid-link' || view === 'not-found'">
          <div class="flex flex-col gap-2 text-center max-w-sm">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              This verification link isn't valid. Requesting a new verification email invalidates older links, so
              make sure you're using the link from the most recent email we sent you.
            </UiText>
          </div>
        </template>

        <!-- Claim already decided -->
        <template v-else-if="view === 'conflict'">
          <div class="flex flex-col gap-2 text-center max-w-sm" data-testid="claim-verify-conflict">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              A decision has already been made on this claim, so email verification is no longer needed. No further
              action is required from you.
            </UiText>
          </div>
        </template>

        <!-- Expired token (410, resend possible) -->
        <template v-else-if="view === 'expired'">
          <div class="flex flex-col gap-2 text-center max-w-sm" data-testid="claim-verify-expired">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              Verification links are only valid for a limited time, but you can request a new one.
            </UiText>
          </div>
        </template>

        <!-- Unexpected error -->
        <template v-else>
          <div class="flex flex-col gap-2 text-center max-w-sm">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              {{ stateTitle }}
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              {{ errorMessage }}
            </UiText>
          </div>
          <UiButton variant="primary" label="Try again" class="min-w-[200px]" @click="verify" />
        </template>

        <!-- Recovery affordances (invalid + expired states) -->
        <template v-if="showRecovery">
          <!-- Exactly one known claim: one-click resend -->
          <div
            v-if="singleContext"
            class="flex w-full max-w-sm flex-col items-center gap-2"
            data-testid="claim-verify-resend-single"
          >
            <UiButton
              variant="primary"
              label="Send a new verification email"
              class="min-w-[200px]"
              :disabled="!resend.canSend.value"
              @click="handleResend(singleContext)"
            />
            <UiText as="p" variant="p-small" class="text-center text-se-gray-500">
              We'll send it to {{ singleContext.email }} for the claim on {{ singleContext.expert_name }}.
            </UiText>
          </div>

          <!-- Multiple known claims: pick which one -->
          <div
            v-else-if="claimContexts.length > 1"
            class="flex w-full max-w-sm flex-col gap-3"
            data-testid="claim-verify-resend-picker"
          >
            <UiText as="p" variant="p-small" class="text-center text-se-gray-600">
              Which claim do you need a new verification email for?
            </UiText>
            <div
              v-for="context in claimContexts"
              :key="`${context.expert_slug}:${context.email}`"
              class="flex items-center justify-between gap-3 rounded-lg border border-se-gray-300 px-4 py-2"
            >
              <div class="flex min-w-0 flex-col">
                <UiText as="span" variant="p-small" class="truncate font-semibold">
                  {{ context.expert_name }}
                </UiText>
                <UiText as="span" variant="caption-normal" class="truncate text-se-gray-700">
                  {{ context.email }}
                </UiText>
              </div>
              <UiButton
                variant="outline"
                label="Send new link"
                size="sm"
                :disabled="!resend.canSend.value"
                @click="handleResend(context)"
              />
            </div>
          </div>

          <!-- Nothing stored on this device -->
          <UiText
            v-else
            as="p"
            variant="p-small"
            class="max-w-sm text-center text-se-gray-600"
            data-testid="claim-verify-resend-none"
          >
            To get a new verification email, return to the expert's profile page and reopen the claim form — it
            will let you resend the link.
          </UiText>

          <UiText
            v-if="resend.successMessage.value"
            as="p"
            variant="p-small"
            class="max-w-sm text-center text-se-green"
            data-testid="claim-verify-resend-success"
          >
            {{ resend.successMessage.value }}
          </UiText>
          <UiText
            v-if="resend.errorMessage.value"
            as="p"
            variant="p-small"
            class="max-w-sm text-center text-se-danger"
            data-testid="claim-verify-resend-error"
          >
            {{ resend.errorMessage.value }}
          </UiText>
          <UiText
            v-if="resend.cooldownSeconds.value > 0"
            as="p"
            variant="p-small"
            class="text-center text-se-gray-500"
          >
            You can request another email in {{ resend.cooldownSeconds.value }}s.
          </UiText>
        </template>

        <UiText v-if="view !== 'verifying'" as="p" variant="p-small" class="text-center text-se-gray-500">
          Have questions?
          <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
        </UiText>
      </div>
    </UiCard>
  </div>
</template>
